import {
  Building2,
  Landmark,
  LineChart,
  ShieldCheck,
  Users,
  Scale,
  KeyRound,
  Cpu,
  ClipboardCheck,
  Globe,
  Layers,
  Stamp,
} from "lucide-react";

// Practice area page copy — client-supplied verbatim content.
// heroImage: set to an image URL to replace the 1600x900 placeholder.
export const PRACTICE_AREAS = [
  {
    slug: "corporate-ma",
    index: "01",
    title: "Corporate & M&A",
    icon: Building2,
    description: "DIFC, ADGM, mainland, and offshore structuring for holding companies, joint ventures, and family offices.",
    heroImage: null,
    heroLine:
      "Structuring that works wherever your business actually operates — UAE, DIFC, ADGM, and across borders.",
    whatWeDo: [
      "Entity selection across UAE free zones, DIFC, ADGM, mainland, and offshore holding jurisdictions, sized to where your business and investors actually are",
      "Cross-border holding structures connecting UAE entities with African and Indian operating companies",
      "Founder and shareholder agreements, equity structuring, cap table design for regionally distributed teams",
      "Family office and trust structuring for multi-jurisdiction asset protection and succession",
    ],
    howWeHelped: [
      "Structured a DIFC Prescribed Company holding vehicle for a digital assets platform, addressing licensing gaps in a proposed yield product before launch.",
      "Advised a UAE free zone trading entity on Qualifying Free Zone Person structuring under UAE Corporate Tax law.",
      "Delivered comparative jurisdiction memos (DIFC, ADGM, DMCC, IFZA) for inbound holding company structuring.",
    ],
  },
  {
    slug: "banking-finance",
    index: "02",
    title: "Banking & Finance",
    icon: Landmark,
    description: "Financing documentation review, security structuring, and Islamic finance considerations.",
    heroImage: null,
    heroLine:
      "Financing structures built for businesses moving capital across borders, not just within one jurisdiction.",
    whatWeDo: [
      "Facility and financing documentation review for corporate borrowers operating across UAE and Africa",
      "Cross-border security and guarantee structuring for regionally active businesses",
      "Islamic finance structuring considerations for UAE and GCC-linked transactions",
      "Regulatory interface for financing arrangements spanning DIFC/ADGM entities and African subsidiaries",
    ],
    howWeHelped: [
      "Advised on financing-adjacent structuring considerations as part of broader digital assets platform work, including debit card and redemption mechanics review.",
    ],
  },
  {
    slug: "capital-markets",
    index: "03",
    title: "Capital Markets",
    icon: LineChart,
    description: "CMA and SCA-facing advisory on listings, disclosure, and regulatory submissions.",
    heroImage: null,
    heroLine: "Regulatory submissions built for issuers and investors operating across the region, not filed in isolation.",
    whatWeDo: [
      "SCA and CMA-facing regulatory advisory for UAE-listed and UAE-regulated entities with regional investor bases",
      "Capital markets submission drafting for businesses raising capital across UAE and adjacent markets",
      "DFSA capital requirement analysis for regionally active regulated categories",
      "Listing and compliance advisory for companies with cross-border shareholder structures",
    ],
    howWeHelped: [
      "Prepared a UAE capital markets industry submission with extensive sourced reference documentation.",
      "Reviewed DFSA Category 3 capital requirement mechanics for a regulated advisory client.",
    ],
  },
  {
    slug: "regulatory-compliance",
    index: "04",
    title: "Regulatory & Compliance (AML-CFT)",
    icon: ShieldCheck,
    description: "Advisory under the UAE's Federal Decree-Law No. 10 of 2025 on AML/CFT (which repealed Federal Decree-Law No. 20 of 2018) and its executive regulations.",
    heroImage: null,
    heroLine: "Compliance frameworks built for businesses that operate across borders, not just within the UAE.",
    whatWeDo: [
      "CDD/EDD frameworks under UAE Federal Decree-Law No. 10 of 2025 and its executive regulations, applied to regionally active clients",
      "Transaction monitoring and suspicious activity reporting protocols for cross-border business models",
      "AML/CFT briefing notes for boards and compliance teams operating across UAE and African jurisdictions",
      "Regulatory query response and correspondence with UAE authorities on behalf of regionally structured clients",
    ],
    howWeHelped: [
      "Produced client-facing briefing notes analyzing recent UAE Federal Decree-Law amendments and Cabinet Resolution updates for regulated entities.",
      "Conducted regulatory due diligence and formal correspondence on behalf of a digital gold platform navigating UAE precious-metals regulation.",
    ],
  },
  {
    slug: "employment-incentives",
    index: "05",
    title: "Employment & Incentives",
    icon: Users,
    description: "UAE Federal and DIFC employment advisory, from contracts and policies to dispute resolution.",
    heroImage: null,
    heroLine: "Employment counsel across UAE, DIFC, and expanding African jurisdictions — not a single-market practice.",
    whatWeDo: [
      "Employment contract drafting under UAE Federal Labour Law, DIFC Employment Law, and growing coverage across Uganda and Kenya",
      "Policy design for multi-jurisdiction employers: leave, termination, end-of-service, and disciplinary frameworks that translate across borders",
      "Multi-jurisdictional employment compliance for regional employers managing distributed teams",
      "Dispute resolution and representation support across UAE and expanding African employment frameworks",
    ],
    howWeHelped: [
      "Co-authored \"Mastering UAE Labour Law: A Practical Guide to Compliance & Strategy\" (122 pages), now referenced in employer compliance training.",
      "Built a UAE employment law reference platform covering Federal and DIFC tracks with interactive calculators, expanding to Uganda and Kenya employment frameworks.",
    ],
    promoTool: {
      eyebrow: "Free Reference Tool",
      title: "Haq HR — employment law, made navigable.",
      body:
        "A free plain-language reference covering UAE, Kenya, and Uganda employment law — notice periods, gratuity and severance calculators, and grounded answers with citations to the underlying statutes. Built for HR teams managing employees across these jurisdictions; not a substitute for counsel on your specific matter.",
      ctaText: "Explore Haq HR",
      ctaUrl: "https://hr.amadilegal.com",
    },
  },
  {
    slug: "restructuring-disputes",
    index: "06",
    title: "Restructuring & Corporate Disputes",
    icon: Scale,
    description: "Board correspondence, regulatory complaints, and pre-litigation positioning support.",
    heroImage: null,
    heroLine: "Clear-headed counsel when a matter turns contentious — across UAE and India.",
    whatWeDo: [
      "Board-level correspondence and governance support during shareholder disputes spanning multiple jurisdictions",
      "Regulatory complaint drafting and correspondence across UAE (RERA, DLD) and Indian tribunal forums (NCLT)",
      "Pre-litigation positioning and settlement correspondence for cross-border corporate disputes",
      "Coordination support for matters before tribunals in both UAE and India",
    ],
    howWeHelped: [
      "Supported board correspondence and regulatory response drafting in a shareholder oppression/mismanagement matter before India's NCLT.",
      "Drafted RERA and DLD complaint materials in a real estate dispute matter.",
    ],
  },
  {
    slug: "real-estate",
    index: "07",
    title: "Real Estate",
    icon: KeyRound,
    description: "Transaction structuring, RERA/DLD correspondence, and dispute support.",
    heroImage: null,
    heroLine: "Property matters, structured and disputed with equal care.",
    whatWeDo: [
      "Real estate transaction structuring and due diligence",
      "RERA and DLD regulatory correspondence",
      "Lease and development agreement review",
      "Real estate dispute support and regulatory complaint drafting",
    ],
    howWeHelped: [
      "Drafted RERA and DLD complaint materials in an active real estate dispute matter.",
    ],
  },
  {
    slug: "tmt",
    index: "08",
    title: "Technology, Media & Data Protection (TMT)",
    icon: Cpu,
    description: "UAE, DPDP, and POPIA compliance frameworks plus legal technology deployment.",
    heroImage: null,
    heroLine:
      "Compliance frameworks built for the jurisdiction you actually operate in — and legal technology built in-house.",
    whatWeDo: [
      "UAE data protection compliance frameworks",
      "India's Digital Personal Data Protection Act (DPDP) advisory",
      "South Africa's POPIA compliance assessments",
      "Legal technology deployment: contract lifecycle management, compliance tracking, and governance tooling",
    ],
    howWeHelped: [
      "Produced a data protection compliance questionnaire for a Southern African market entry.",
      "Designed and deployed a proprietary contract lifecycle management and legal governance platform, covering repository, clause library, workflows, and AI-assisted risk analysis.",
    ],
  },
  {
    slug: "governance-risk",
    index: "09",
    title: "Governance, Risk & Board Advisory",
    icon: ClipboardCheck,
    description: "Governance manuals, board materials, ESG frameworks, entity/UBO registers.",
    heroImage: null,
    heroLine: "Governance documentation that boards actually use.",
    whatWeDo: [
      "Corporate governance manual drafting",
      "Board meeting materials, minutes frameworks, and resolution templates",
      "ESG framework advisory",
      "Entity register, cap table, and UBO register structuring",
    ],
    howWeHelped: [
      "Drafted a full corporate governance manual and board meeting presentation suite for a multinational client's Africa cluster.",
      "Built a governance and board meetings module, entity register, cap table, and shareholders/UBO register as part of a proprietary legal technology platform.",
    ],
  },
  {
    slug: "cross-border-africa",
    index: "10",
    title: "Cross-Border & Africa Corridor",
    icon: Globe,
    description: "UAE–Africa–India corridor incorporation, tax structuring, and regulatory navigation across 20+ jurisdictions.",
    heroImage: null,
    heroLine: "Structuring that works on both ends of the corridor.",
    whatWeDo: [
      "Incorporation and regulatory navigation across Kenya, Tanzania, Uganda, Rwanda, South Africa, and Mauritius",
      "Cross-border tax structuring (HoldCo–OpCo models)",
      "Regulatory correspondence with African competition and sector regulators",
      "Shareholders' agreements under multiple African legal systems",
    ],
    howWeHelped: [
      "Advised on Tanzania incorporation matters via cross-border legal correspondence.",
      "Produced a HoldCo-OpCo cross-border tax structuring memorandum spanning US, Delaware, and South Africa entities.",
      "Drafted a shareholders' agreement under Mauritius law for a multi-jurisdiction African corporate cluster.",
    ],
  },
  {
    slug: "corporate-structuring-incorporation",
    index: "11",
    title: "Corporate Structuring & Incorporation",
    icon: Layers,
    description: "Jurisdiction and entity selection, formation, licensing, and group structuring across the Middle East, Africa, Europe, and India.",
    heroImage: null,
    heroLine:
      "Strategic entity structuring and formation across the Middle East, Africa, Europe, and India.",
    overview: [
      "Expanding into a new market starts with the right structure. We advise founders, corporates, and investors on where and how to establish a presence — from jurisdiction and entity selection through to licensing, registration, and group structuring — so the foundation supports the business you're building, not just the one you're launching.",
      "AMADI Legal advises on company formation and corporate structuring across the UAE, wider GCC, Africa, Europe, and India. Our role goes beyond filing paperwork: we help clients weigh jurisdiction against regulatory exposure, tax residency, capital mobility, and long-term commercial strategy, then execute the formation efficiently once the structure is set.",
    ],
    whatWeDo: [
      "Jurisdiction & Structure Advisory — Comparative analysis of mainland, free zone, and offshore options (UAE/GCC), and equivalent structuring decisions across African, European, and Indian markets",
      "Entity Formation & Licensing — End-to-end incorporation, trade licensing, and regulatory registration",
      "Group & Holding Structures — Cross-border holding company design for multinational groups, joint ventures, and investment vehicles",
      "Sector-Specific Licensing — Regulatory approvals for financial services, technology, trading, and professional services entities",
      "Foreign Direct Investment Structuring — Advising international investors entering Middle East, Africa, and India markets on the most efficient route to market",
      "Post-Incorporation Transition — Handover into ongoing corporate maintenance and secretarial support (see Company Secretarial Services)",
    ],
    whyUs:
      "Operating across 20+ jurisdictions, our team combines DIFC-based regulatory depth with regional and cross-border structuring experience — giving clients a single point of advice whether they're setting up one entity or a multi-jurisdiction group structure.",
    related: [
      { title: "Company Secretarial Services", slug: "company-secretarial-services" },
    ],
    howWeHelped: [],
  },
  {
    slug: "company-secretarial-services",
    index: "12",
    title: "Company Secretarial Services",
    icon: Stamp,
    description: "Ongoing governance, statutory compliance, and secretarial support across the Middle East, Africa, Europe, and India.",
    heroImage: null,
    heroLine:
      "Governance, compliance, and statutory support for companies operating across the Middle East, Africa, Europe, and India.",
    overview: [
      "Good governance is a discipline, not a one-time task. We provide ongoing company secretarial support that keeps entities compliant, boards properly advised, and statutory obligations met — across single jurisdictions and complex multi-entity group structures alike.",
      "AMADI Legal provides company secretarial and corporate governance support to companies, boards, and group structures once formation is complete. We act as an extension of the legal and governance function — managing statutory obligations so management can focus on the business.",
    ],
    whatWeDo: [
      "Statutory Compliance — Maintenance of statutory registers, filings, and compliance calendars across UAE (including DIFC/ADGM), other MEA jurisdictions, Europe, and India",
      "Board & Shareholder Support — Preparation of resolutions, meeting notices, minutes, and governance documentation for boards and shareholders",
      "UBO & Beneficial Ownership Reporting — Ongoing compliance with beneficial ownership disclosure requirements",
      "Registered Agent & Registered Office Services — Where applicable under local regulatory frameworks",
      "Corporate Governance Advisory — Guidance for boards on governance structure, delegation of authority, and best-practice compliance frameworks",
      "Group Governance Coordination — Consistent secretarial support across multi-jurisdiction group structures",
    ],
    whyUs:
      "For groups operating across several jurisdictions, inconsistent secretarial practice is a real compliance risk. We give clients a single, coordinated view of governance obligations across their full structure — rather than managing each entity in isolation.",
    related: [
      { title: "Corporate Structuring & Incorporation", slug: "corporate-structuring-incorporation" },
    ],
    howWeHelped: [],
  },
];

export const getPracticeArea = (slug) =>
  PRACTICE_AREAS.find((p) => p.slug === slug);
